import React from "react";
import { useFetchCurrentOrdersQuery } from "../store";
import { OrderStatusText, OrderTypeText } from "../types";
import { useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import Skeleton from "../components/common/Skeleton";

const CurrentOrders: React.FC = () => {
  const navigate = useNavigate();
  const { data: orders, error, isLoading } = useFetchCurrentOrdersQuery();

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto p-6 flex flex-col gap-6">
        <Skeleton
          className="w-48 h-8 mb-4"
          times={1}
        />
        <Skeleton
          className="w-full h-40 rounded-xl"
          times={3}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <h3 className="text-xl font-semibold text-red-500">
          Une erreur est survenue
        </h3>
        <p className="text-gray-500">
          Impossible de charger vos commandes en cours.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 flex flex-col items-center py-6 px-4">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-4 mb-8">
          <button
            className="p-2 hover:bg-white rounded-full transition-colors shadow-sm bg-white border border-gray-100"
            onClick={() => navigate("/")}
          >
            <IoMdArrowRoundBack className="text-xl text-gray-800" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900">
            Commandes en cours
          </h2>
        </div>

        {!orders || orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-3xl">🍽️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucune commande en cours
            </h3>
            <p className="text-gray-500 mb-6">
              Vous n&apos;avez pas de commande active pour le moment.
            </p>
            <button
              onClick={() => navigate("/restaurants")}
              className="px-6 py-3 bg-black text-white rounded-xl font-semibold hover:bg-gray-900 transition-shadow shadow-md hover:shadow-lg"
            >
              Commander maintenant
            </button>
          </div>
        ) : (
          <ul className="flex flex-col gap-4">
            {orders.map((order) => (
              <li
                key={order._id}
                className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group cursor-pointer"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                      {order.restaurant}
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      Commande #{order.displayId}
                    </span>
                  </div>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full border border-yellow-200 uppercase tracking-wide">
                    {OrderStatusText[order.status]}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4 bg-gray-50 p-3 rounded-lg">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Heure
                    </span>
                    <span className="font-medium text-gray-900">
                      {order.createdAt}
                    </span>
                  </div>
                  <div className="w-px h-8 bg-gray-200"></div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Type
                    </span>
                    <span className="font-medium text-gray-900">
                      {OrderTypeText[order.orderType]}
                    </span>
                  </div>
                  <div className="w-px h-8 bg-gray-200"></div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-400 uppercase font-semibold">
                      Articles
                    </span>
                    <span className="font-medium text-gray-900">
                      {order.cartSize} produit{order.cartSize > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <span className="text-sm text-gray-500 font-medium">
                    Total payé
                  </span>
                  <span className="text-lg font-bold text-primary">
                    {order.paymentInfo.totalPrice.text}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CurrentOrders;
